/**
 * Sync state types and utilities extracted from HomeComponent.
 */

export type SyncState = 'ok' | 'syncing' | 'offline' | 'error';

/**
 * Connectivity level shown by the footer status indicator (Home/View/Edit).
 * Distinguishes "no network at all" from "network is up but the app's server
 * isn't responding" — both are shown with the same red icon, but with a
 * different, honest tooltip/message rather than a single generic "offline".
 */
export type ConnectivityLevel = 'online' | 'offline' | 'server-unreachable';

export interface ConnectivityDisplay {
  level: ConnectivityLevel;
  /** true only when the browser is online AND the server responded to the last sync attempt. */
  fullyOnline: boolean;
  icon: string;
  label: string;
}

/**
 * Combines the browser's own online/offline signal with whether the server actually
 * responded to the last sync attempt (SyncCoordinatorService.serverReachable$) into a
 * single status suitable for a status indicator. The browser can report "online" while
 * the app's server is completely unreachable (e.g. powered off) — that must never show
 * as a green "online" icon.
 */
export function resolveConnectivityDisplay(isOnline: boolean, serverReachable: boolean): ConnectivityDisplay {
  if (!isOnline) {
    return { level: 'offline', fullyOnline: false, icon: 'signal_disconnected', label: 'NotesApp is OFF-LINE!' };
  }
  if (!serverReachable) {
    return {
      level: 'server-unreachable',
      fullyOnline: false,
      icon: 'cloud_off',
      label: 'Server is not responding — changes are saved locally and will sync once it is reachable.',
    };
  }
  return { level: 'online', fullyOnline: true, icon: 'cloud_done', label: 'Online mode' };
}

export interface SyncStatus {
  couchdb: SyncState;
  indexeddb: SyncState;
  lastSynced: Date | null;
}

/**
 * Pure sync state logic extracted from HomeComponent.
 */
export class SyncStateLogic {
  /**
   * Refresh sync status based on online status and whether the server actually
   * responded to the last sync attempt (defaults to true so existing call sites
   * that haven't wired up server-reachability tracking are unaffected).
   */
  static refreshSyncStatus(isOnline: boolean, serverReachable = true): SyncStatus {
    try {
      return {
        couchdb: !isOnline || !serverReachable ? 'offline' : 'ok',
        indexeddb: 'ok',
        lastSynced: null,
      };
    } catch {
      return {
        couchdb: 'error',
        indexeddb: 'ok',
        lastSynced: null,
      };
    }
  }

  /**
   * While syncing, set transient 'syncing' states without
   * losing offline/error info permanently.
   */
  static setTransientStates(currentStatus: SyncStatus, state: SyncState): SyncStatus {
    const updated = { ...currentStatus };

    if (updated.couchdb !== 'offline' && updated.couchdb !== 'error') {
      updated.couchdb = state;
    }
    if (updated.indexeddb !== 'offline' && updated.indexeddb !== 'error') {
      updated.indexeddb = state;
    }

    return updated;
  }

  /**
   * Set sync status to success state
   */
  static setSuccessState(currentStatus: SyncStatus): SyncStatus {
    return {
      ...currentStatus,
      couchdb: 'ok',
      indexeddb: 'ok',
      lastSynced: new Date(),
    };
  }

  /**
   * Set sync status to error state
   */
  static setErrorState(currentStatus: SyncStatus): SyncStatus {
    return {
      ...currentStatus,
      couchdb: 'error',
      indexeddb: 'error',
    };
  }
}
